import mergeObjects from '@bybeam/platform-lib/answers/mergeObjects';
import {
  ApplicationConfig,
  ApplicationQuestion,
  ApplicationSection,
  CommonQuestionKeys,
  FieldType,
  IntroPage,
  PaymentMethod,
  TextInputType,
} from '@bybeam/platform-types';
import { YesNoOptions } from '@platform-ui/application/utils/index.js';
import { eq } from '../ast/utils.js';
import STANDARD_COMPONENTS from './standards/index.js';
import { SpecialSectionKeys } from './standards/types.js';

const ZIPS = [
  '10451',
  '10452',
  '10453',
  '10454',
  '10455',
  '10456',
  '10457',
  '10458',
  '10459',
  '10460',
  '10461',
  '10462',
  '10463',
  '10466',
  '10467',
  '10468',
  '10469',
  '10470',
  '10472',
  '10473',
  '10474',
  '10475',
];

export const QUESTIONS: { [key: string]: ApplicationQuestion } = {
  [CommonQuestionKeys.ZIP_CODE]: {
    key: CommonQuestionKeys.ZIP_CODE,
    copy: { title: 'What is your ZIP code?' },
    fields: [
      {
        type: FieldType.Dropdown,
        key: CommonQuestionKeys.ZIP_CODE,
        copy: 'Zip Code',
        options: ZIPS.map((zip) => ({ label: zip, value: zip })),
        props: { searchable: true },
      },
    ],
  },
  childcareBusinessType: {
    key: 'childcareBusinessType',
    copy: { title: 'Which type of care best describes your childcare business?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'childcareBusinessType',
        options: [
          { label: 'Family Child Daycare', value: 'family-child-daycare' },
          { label: 'Group Family Daycare', value: 'group-family-daycare' },
          { label: 'Other', value: 'other' },
        ],
      },
      {
        type: FieldType.Typography,
        key: 'childcareBusinessType.question',
        dynamicLogic: eq('childcareBusinessType', 'other'),
        copy: 'Please specify below:',
        props: { variant: 'largeBody', alignment: 'center' },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'otherChildcareBusinessType',
        dynamicLogic: eq('childcareBusinessType', 'other'),
        copy: 'Type of Care',
      },
    ],
  },
  daycareLicense: {
    key: 'daycareLicense',
    copy: {
      title:
        'Please enter your Family Child Daycare or Group Family Daycare License Number and Date Issued.',
    },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'daycareLicense.number',
        copy: 'License Number',
      },
      {
        type: FieldType.Date,
        key: 'daycareLicense.dateIssued',
        copy: 'Date Issued',
      },
    ],
  },
  estimatedChildcareLength: {
    key: 'estimatedChildcareLength',
    copy: { title: 'How long do you intend to earn income from caring for children?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'estimatedChildcareLength',
        options: [
          { label: 'Less than 6 months', value: 'less-than-6-months' },
          { label: '7 months to a year', value: '7-months-to-a-year' },
          { label: 'More than a year', value: 'more-than-a-year' },
        ],
      },
    ],
  },
  approvedForPayments: {
    key: 'approvedForPayments',
    copy: {
      title:
        'Are you approved by the NYS Office of Children and Family Services to receive payments on the behalf of children in families who receive the Child Care Assistance Program (subsidy)?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'approvedForPayments',
        options: YesNoOptions,
      },
    ],
  },
  caringForChildren: {
    key: 'caringForChildren',
    copy: { title: 'Are you currently caring for children?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'caringForChildren',
        options: YesNoOptions,
      },
    ],
  },
  agreesToReport: {
    key: 'agreesToReport',
    copy: {
      title:
        'If selected to participate in the TPP and if your caregiving situation changes during the program, do you agree to report when requested and provide updates?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'agreesToReport',
        options: YesNoOptions,
      },
    ],
  },
  applyingPublicAssistance: {
    key: 'applyingPublicAssistance',
    copy: {
      title:
        'Do you currently receive or are you in the process of applying for any other public assistance (SNAP, TANF, Supplemental Security Income (SSI), etc.?',
      intro: 'Your response to this question will not affect your eligibility to apply.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'applyingPublicAssistance',
        options: YesNoOptions,
      },
    ],
  },
  understandsEligibilityRisk: {
    key: 'understandsEligibilityRisk',
    dynamicLogic: eq('applyingPublicAssistance', 'yes'),
    copy: {
      title:
        'If accepted into this program, do you understand that there is a risk that the income you will receive will impact your eligibility to receive other public assistance?',
      intro: `
If selected to participate in the program, your eligibility to receive other public benefits 
may be impacted. A member of the All Our Kin team will conduct benefits counseling with 
recipients to ensure understanding of potential impacts to benefits.

If you have further questions about the program, reach out to {PARTNER_EMAIL}.`,
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'understandsEligibilityRisk',
        options: YesNoOptions,
      },
    ],
  },
  preferredLanguage: {
    key: 'preferredLanguage',
    copy: {
      title:
        'What is your preferred language of communication for written documents (i.e. SMS messages, forms, etc.)?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'preferredLanguage',
        options: [
          { label: 'English', value: 'english' },
          { label: 'Spanish', value: 'spanish' },
          { label: 'Other', value: 'other' },
        ],
      },
      {
        type: FieldType.Typography,
        key: 'preferredLanguage.question',
        dynamicLogic: eq('preferredLanguage', 'other'),
        copy: 'Please specify below:',
        props: { variant: 'largeBody', alignment: 'center' },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'otherPreferredLanguage',
        dynamicLogic: eq('preferredLanguage', 'other'),
        copy: 'Preferred Language',
      },
    ],
  },
  preferredPhoneNumber: {
    key: 'preferredPhoneNumber',
    copy: {
      title: 'What is your preferred phone number to contact you on?',
      intro: `Please provide your country code, area code, and phone number. This is 
where important information about the program will be sent, so please select a number 
you regularly have access to and can receive SMS messages on.`,
    },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Phone,
        key: 'preferredPhoneNumber',
        copy: 'Phone Number',
        props: { helperText: 'Example: +1 (219) 873-2341' },
        validation: { required: false },
      },
    ],
  },
  preferredEmailAddress: {
    key: 'preferredEmailAddress',
    copy: {
      title: 'What is your preferred email address?',
      intro:
        'This is where important information about the program will be sent, so please make sure you regularly have access to this email.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'preferSecondaryEmail',
        options: [
          { label: '{APPLICANT_EMAIL}', value: 'no' },
          { label: 'Other', value: 'yes' },
        ],
      },
      {
        type: FieldType.Typography,
        key: 'preferredEmailAddress.question2',
        dynamicLogic: eq('preferSecondaryEmail', 'yes'),
        copy: 'Please provide your preferred email address below:',
        props: { variant: 'largeBody', alignment: 'center' },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Email,
        key: 'preferredEmailAddress',
        dynamicLogic: eq('preferSecondaryEmail', 'yes'),
        copy: 'Email Address',
      },
    ],
  },
  preferredPaymentMethod: {
    key: CommonQuestionKeys.PREFERRED_PAYMENT_METHOD,
    copy: { title: 'If selected to participate, what is your preferred method of payment?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: CommonQuestionKeys.PREFERRED_PAYMENT_METHOD,
        options: [
          {
            label: 'ACH/Direct Deposit',
            description:
              'We recommend using this option if you have access to a bank account. You should receive your funds in 1-3 business days.',
            value: PaymentMethod.DirectDeposit,
          },
          {
            label: 'Physical Prepaid Debit Card',
            description:
              'Physical cards will take longer than the other payment methods. This can take more than 10 business days to receive.',
            value: PaymentMethod.PhysicalCard,
          },
          {
            label: 'Virtual Prepaid Debit Card',
            description:
              'Virtual cards are a faster method to receive your funds. This should take less than 3 business days.',
            value: PaymentMethod.VirtualCard,
          },
          {
            label: 'I’m not sure',
            description:
              'We will follow up with you to decide on the best way for you to receive payment.',
            value: 'not-sure',
          },
        ],
      },
    ],
  },
  applicationAssistanceHelper: {
    key: 'submission.assistance.signature',
    copy: { title: 'Did you receive assistance completing this application?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'submission.assistance.receivedAssistance',
        options: YesNoOptions,
      },
      {
        type: FieldType.Typography,
        key: 'submission.assistance.question',
        dynamicLogic: eq('submission.assistance.receivedAssistance', 'yes'),
        copy: 'Please enter the case manager’s name/digital signature below:',
        props: { variant: 'largeBody', alignment: 'center' },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: CommonQuestionKeys.SIGNATURE,
        dynamicLogic: eq('submission.assistance.receivedAssistance', 'yes'),
        copy: 'Digital Signature',
        props: { helperText: 'Ex: Micha Lynn Johnson' },
      },
    ],
  },
  isPrimaryCaregiver: {
    key: 'isPrimaryCaregiver',
    copy: { title: 'Please confirm this statement:' },
    fields: [
      {
        type: FieldType.Checkbox,
        key: 'isPrimaryCaregiver',
        copy: 'I am or I intend to be the primary caregiver for the child(ren) in my care, responsible for their direct care and experience.',
      },
    ],
  },
  [CommonQuestionKeys.AFFIRMATION]: {
    key: CommonQuestionKeys.AFFIRMATION,
    copy: { title: 'Please confirm this statement:' },
    fields: [
      {
        type: FieldType.Checkbox,
        key: CommonQuestionKeys.AFFIRMATION,
        copy: 'I attest that the information I provided in this application is accurate to the best of my knowledge.',
      },
    ],
  },
};

const SECTIONS: ApplicationSection[] = [
  {
    key: 'personalInfo',
    name: 'Personal Info',
    overview: {
      title: 'Let’s start with some personal information about you',
      description: 'In this section, you’ll let us know about your home address and date of birth.',
    },
    questionGroups: [
      {
        key: 'personalInfo',
        name: 'Personal Info',
        questions: [
          QUESTIONS[CommonQuestionKeys.ZIP_CODE],
          STANDARD_COMPONENTS.definitions.questions[CommonQuestionKeys.MAILING_ADDRESS],
          STANDARD_COMPONENTS.definitions.questions[CommonQuestionKeys.DATE_OF_BIRTH],
        ],
      },
    ],
  },
  {
    key: 'providerInfo',
    name: 'Provider Info',
    overview: {
      title: 'Now, let’s learn about your provider information',
      description:
        'In this section, you’ll let us know about your eligibility by answering specific questions about your current situation.',
    },
    questionGroups: [
      {
        key: 'providerInfo',
        name: 'Provider Info',
        questions: [
          QUESTIONS.childcareBusinessType,
          QUESTIONS.daycareLicense,
          QUESTIONS.estimatedChildcareLength,
          QUESTIONS.approvedForPayments,
          QUESTIONS.caringForChildren,
          QUESTIONS.agreesToReport,
          QUESTIONS.applyingPublicAssistance,
          QUESTIONS.understandsEligibilityRisk,
        ],
      },
    ],
  },
  {
    key: 'preferences',
    name: 'Preferences',
    overview: {
      description: `
In this section, you’ll share your preferred language of communication for written
documents, phone number, email address and method of payment.`, // TODO(SD): Tell Mel about change to copy.
      title: 'Now it’s time to find out a little more about your preferences',
    },
    questionGroups: [
      {
        key: 'preferences',
        name: 'Preferences',
        questions: [
          QUESTIONS.preferredLanguage,
          QUESTIONS.preferredPhoneNumber,
          QUESTIONS.preferredEmailAddress,
          QUESTIONS.preferredPaymentMethod,
        ],
      },
    ],
  },
  mergeObjects(
    [
      STANDARD_COMPONENTS.definitions.sections[SpecialSectionKeys.Review],
      {
        questionGroups: [
          // Drop the first question and replace it with this one
          { questions: [null, QUESTIONS.applicationAssistanceHelper] },
          // Drop the first question and replace it with this one
          { questions: [null, QUESTIONS.isPrimaryCaregiver, QUESTIONS.affirmation] },
        ],
      } as ApplicationSection,
    ],
    'merge',
  ),
];

const CONFIG: ApplicationConfig = {
  introPages: [
    {
      key: 'what-is-it',
      layout: 'welcome',
      title: 'What is the Thriving Providers Project?',
      body: `All Our Kin is participating in the Thriving Providers Project, which provides 
direct cash assistance that supports home-based child care providers. These funds do not 
require repayment. If direct cash assistance could help you pay for your needs, you may be 
eligible to submit an application through All Our Kin.`,
    },
    {
      key: 'stanford-study',
      layout: 'default',
      title: 'Stanford Center on Early Childhood',
      body: `If selected as a Thriving Providers Project (TPP) participant, you will be invited to participate in an evaluation with the Stanford Center on Early Childhood, 
      that aims to learn more about how TPP can support the needs and well-being of home-based child care providers, the children they care for, and their families.  \n\n
      
  The evaluation includes brief, compensated surveys about your experiences. This information will be used by the TPP team to learn about how, 
  and in what ways, the program is helpful—this will allow them to continue improving the program, with the goal of potentially offering it to even more child care providers in the future. 
  More details will be provided to selected participants during enrollment.`,
    },
    mergeObjects(
      [
        STANDARD_COMPONENTS.definitions.introPages.ApplicationProcess,
        {
          steps: [
            undefined,
            {
              title: 'It takes around 15 minutes to complete',
              description:
                'You can expect to spend 15 minutes or less filling out your application.',
            },
            undefined,
            undefined,
            {
              title: 'Get a decision',
              icon: 'mail',
              description:
                'We will review your application and notify you by email of the decision as soon as possible.',
            },
          ],
        } as IntroPage,
      ],
      'merge',
    ),
  ],
  overrides: {
    statuses: [
      {
        status: 'Application',
        label: '',
        message:
          'You could be found eligible to receive direct cash assistance up to {MAX_AMOUNT}. These funds do not require repayment and are intended to help home-based child care providers.',
      },
    ],
  },
  sections: SECTIONS,
};

export default CONFIG;
